/*
 * (c) Copyright 2024 Calabrio, Inc.
 * All Rights Reserved. www.calabrio.com LICENSED MATERIALS
 * Property of Calabrio, Inc., Minnesota, USA
 *
 * No part of this publication may be reproduced, stored or transmitted,
 * in any form or by any means (electronic, mechanical, photocopying,
 * recording or otherwise) without prior written permission from Calabrio, Inc.
 */

import Vue from 'vue';
import axios from 'axios';
import hash from 'object-hash';

export default {
  API: {
    // http://localhost/studio/#/autoQA/aiEvaluationSummary/?host=c1-wysdom.wysdom.calabrio.dev&sid=HZ5A442D2529B44ABF8F350BEA4E4DC093&options=
    // INTERACTIONS_CHAT_FILTERS: '/interactionsAnalytics/chatFilters',
    SMART_TAG_CHAT_FILTERS: '/smartTags/smartTagsFilters',
    // SMART_TAG_CHAT_FILTERS: '/autoQA/chatFilters',
    SMART_TAG_DASHBOARD: '/smartTags/smartTagDashboard',
    SMART_TAG_DASHBOARD_ASSOCIATED_TOPICS: '/smartTags/smartTagAssociatedTopics',
    SMART_TAG_DASHBOARD_TOPIC_CONVERSATION: '/smartTags/smartTagTopicConversationDetails',
    SMART_TAG_DASHBOARD_CONVERSATION_DETAILS: '/smartTags/smartTagConversationDetails',
    // SMART_TAG_DASHBOARD_ASSOCIATED_PHRASE_HIT_CATEGORIES: '/interactionsAnalytics/associatedPhraseHitCategories',
    SMART_TAG_DASHBOARD_ASSOCIATED_PHRASE_HIT_CATEGORIES: '/smartTags/smartTagPhraseCategoryStats',
    SMART_TAG_DASHBOARD_ASSOCIATED_PHRASE_HIT: '/smartTags/smartTagPhraseStats',
    SMART_TAG_DASHBOARD_ASSOCIATED_PHRASE_CONVERSATION: '/smartTags/smartTagPhraseConversationDetails',

    SMART_TAG_DASH_TABLE_DETAIL_SMART_TAG_TREND: '/smartTags/smartTagTopicTrend',
  },

  cachedHash: {},
  cancelTokens: [],

  getRequest(authUser, url, headers) {
    if (authUser.accessToken) {
      if (headers == null) headers = {};
      headers['Authorization'] = 'Bearer ' + authUser.accessToken;
      headers['Api-Configs'] = JSON.stringify(authUser.config.api.configs);
      if(authUser.sessionId) {
        headers['Auth-Host'] = authUser.authHost || "";
        headers['Auth-Session-Id'] = authUser.sessionId;
        headers['Auth-Product-Name'] = authUser.productName || "";
        headers['Auth-Options'] = authUser.authOptions || "";
      }

      return axios.get(url, { headers: headers, timeout: 300000 });
    } else {
      return null;
    }
  },

  postRequest(authUser, url, payload, headers) {
    if (authUser.accessToken) {
      if (headers == null) headers = {};
      headers['Authorization'] = 'Bearer ' + authUser.accessToken;
      headers['Api-Configs'] = JSON.stringify(authUser.config.api.configs);
      if(authUser.sessionId) {
        headers['Auth-Host'] = authUser.authHost || "";
        headers['Auth-Session-Id'] = authUser.sessionId;
        headers['Auth-Product-Name'] = authUser.productName || "";
        headers['Auth-Options'] = authUser.authOptions || "";
      }

      let cancelTokenKey = url;
      if (this.cancelTokens[cancelTokenKey]) {
        // cancel request
        this.cancelTokens[cancelTokenKey].cancel();
      }
      this.cancelTokens[cancelTokenKey] = axios.CancelToken.source();

      let options = {
        timeout: 300000, // timeout in 5 mins
        headers: headers,
        cancelToken: this.cancelTokens[cancelTokenKey].token,
      };
      return axios.post(url, payload, options);
    } else {
      return null;
    }
  },

  fetchChatFilters(authUser) {
    let url = Vue.WyConstants.SERVER.SMART_TAGS_HOST + this.API.SMART_TAG_CHAT_FILTERS;
    return this.getRequest(authUser, url);
  },

  fetchSmartTagsDashboard(authUser, options, payload) {
    delete payload.topics;

    let url = Vue.WyConstants.SERVER.SMART_TAGS_HOST + this.API.SMART_TAG_DASHBOARD;
    let hashName = `fetchSmartTagsDashboard`;
    let payloadHash = hash(payload);
    if ((options && options.forceRefresh) || this.cachedHash[hashName] != payloadHash) {
      this.cachedHash[hashName] = payloadHash;
      return new Promise(async (resolve) => {
        let res = await this.postRequest(authUser, url, payload);
        resolve(res);
      });
    } else {
      return new Promise(async (resolve) => {
        resolve(true);
      });
    }
  },

  fetchSmartTagTrend(user, payload) {
    let url = Vue.WyConstants.SERVER.SMART_TAGS_HOST + this.API.SMART_TAG_DASH_TABLE_DETAIL_SMART_TAG_TREND;
    return this.postRequest(user, url, payload);
  },

  fetchAssociatedTopics(authUser, payload) {
    let url = Vue.WyConstants.SERVER.SMART_TAGS_HOST + this.API.SMART_TAG_DASHBOARD_ASSOCIATED_TOPICS;
    delete payload.from;
    return this.postRequest(authUser, url, payload);
  },

  fetchContactsDetails(authUser, payload) {
    let url = Vue.WyConstants.SERVER.SMART_TAGS_HOST + this.API.SMART_TAG_DASHBOARD_CONVERSATION_DETAILS;
    delete payload.from;
    return this.postRequest(authUser, url, payload);
  },

  fetchAssociatedTopicContacts(authUser, payload) {
    let url = Vue.WyConstants.SERVER.SMART_TAGS_HOST + this.API.SMART_TAG_DASHBOARD_TOPIC_CONVERSATION;
    delete payload.from;
    return this.postRequest(authUser, url, payload);
  },

  fetchAssociatedTopicPhraseHitCategories(authUser, payload) {
    let url = Vue.WyConstants.SERVER.SMART_TAGS_HOST + this.API.SMART_TAG_DASHBOARD_ASSOCIATED_PHRASE_HIT_CATEGORIES;
    delete payload.from;
    return this.postRequest(authUser, url, payload);
  },
  
  fetchAssociatedTopicPhraseHitByCategoryName(authUser, payload) {
    let url = Vue.WyConstants.SERVER.SMART_TAGS_HOST + this.API.SMART_TAG_DASHBOARD_ASSOCIATED_PHRASE_HIT;
    delete payload.from;
    return this.postRequest(authUser, url, payload);
  },

  fetchAssociatedTopicPhraseHitContacts(authUser, payload) {
    let url = Vue.WyConstants.SERVER.SMART_TAGS_HOST + this.API.SMART_TAG_DASHBOARD_ASSOCIATED_PHRASE_CONVERSATION;
    delete payload.from;
    return this.postRequest(authUser, url, payload);
  },
};
